import { ArrowRight, Check, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const TIERS = [
  {
    name: 'Starter',
    price: 'Free',
    period: 'forever',
    badge: null,
    disabled: false,
    description: 'Everything you need to start tracking and journaling your trades.',
    cta: 'Create Free Account',
    ctaTo: '/login?mode=register',
    features: [
      'Unlimited manual trade logging',
      'Trading journal with rich notes',
      '30-day performance analytics',
      'P&L calendar',
      'Trade playbooks',
      '1 broker connection',
      'Community support',
    ],
    missingFeatures: [
      'AI edge analysis',
      'Behavioral pattern detection',
      'Weekly digest email',
      'Priority support',
    ],
  },
  {
    name: 'Trader',
    price: '$19',
    period: '/month',
    badge: 'Most Popular',
    disabled: true,
    description: 'Full analytics and AI edge analysis for serious traders.',
    cta: 'Coming Soon',
    ctaTo: null,
    features: [
      'Everything in Starter',
      'AI edge analysis',
      'Unlimited broker connections',
      'Full analytics history',
      'Win rate heatmaps',
      'Trade export (CSV)',
      'Email support',
    ],
    missingFeatures: [
      'Behavioral pattern detection',
      'Weekly digest email',
    ],
  },
  {
    name: 'Coach',
    price: '$39',
    period: '/month',
    badge: 'Full Power',
    disabled: true,
    description: 'Behavioral AI that finds exactly what is costing you money.',
    cta: 'Coming Soon',
    ctaTo: null,
    features: [
      'Everything in Trader',
      'Behavioral AI (revenge trading, sizing drift, etc.)',
      'Weekly performance digest email',
      'Pattern alerts',
      'Local AI (Ollama / LM Studio)',
      'API access',
      'Priority support',
    ],
    missingFeatures: [],
  },
];

function TierCard({ tier, isAuthenticated }) {
  const isMiddle = tier.name === 'Trader';
  const isDisabled = tier.disabled;

  return (
    <div
      className={`relative rounded-2xl p-8 flex flex-col ${
        isDisabled
          ? 'opacity-50 bg-slate-800/20 border border-slate-700/30'
          : isMiddle
          ? 'bg-emerald-500/10 border-2 border-emerald-500/50 shadow-xl shadow-emerald-500/10'
          : 'bg-slate-800/30 border border-slate-700/40'
      }`}
    >
      {tier.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full ${
          isDisabled ? 'bg-slate-700 text-slate-400' : 'bg-emerald-500 text-white'
        }`}>
          {tier.badge}
        </div>
      )}

      {isDisabled && (
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-slate-700/60 border border-slate-600/50 rounded-full px-2.5 py-1">
          <Lock size={10} className="text-slate-400" />
          <span className="text-slate-400 text-xs font-medium">Coming soon</span>
        </div>
      )}

      <div className="mb-6">
        <div className="text-slate-400 text-sm font-semibold uppercase tracking-widest mb-2">{tier.name}</div>
        <div className="flex items-end gap-1 mb-3">
          <span className="text-4xl font-black text-white">{tier.price}</span>
          {tier.period && <span className="text-slate-500 text-sm mb-1">{tier.period}</span>}
        </div>
        <p className="text-slate-400 text-sm leading-relaxed">{tier.description}</p>
      </div>

      {isDisabled ? (
        <div className="w-full py-3 rounded-xl bg-slate-700/50 text-slate-500 font-semibold text-sm text-center mb-6 cursor-not-allowed">
          {tier.cta}
        </div>
      ) : (
        <Link
          to={tier.ctaTo}
          className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3 rounded-xl transition-colors text-sm mb-6"
        >
          {tier.cta} <ArrowRight size={14} />
        </Link>
      )}

      <div className="space-y-3 flex-1">
        {tier.features.map((f) => (
          <div key={f} className="flex items-start gap-2.5">
            <Check size={14} className={`flex-shrink-0 mt-0.5 ${isDisabled ? 'text-slate-600' : 'text-emerald-400'}`} />
            <span className={`text-sm ${isDisabled ? 'text-slate-600' : 'text-slate-300'}`}>{f}</span>
          </div>
        ))}
        {tier.missingFeatures.map((f) => (
          <div key={f} className="flex items-start gap-2.5 opacity-40">
            <div className="w-3.5 h-px bg-slate-600 flex-shrink-0 mt-2" />
            <span className="text-slate-500 text-sm">{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PricingSection({ isAuthenticated }) {
  return (
    <div id="pricing" className="bg-[#0d1e35] py-24 px-6 border-y border-slate-800/40">
      <div className="max-w-5xl mx-auto">

        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-emerald-300 text-xs font-semibold tracking-widest uppercase">Pricing</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Start free. Upgrade when you're ready.</h2>
          <p className="text-slate-400 max-w-xl mx-auto text-lg">
            Starter is free forever. Paid plans with full AI and behavioral analysis are launching soon.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {TIERS.map((tier) => (
            <TierCard key={tier.name} tier={tier} isAuthenticated={isAuthenticated} />
          ))}
        </div>

        <p className="text-center text-slate-600 text-sm">
          Paid plans require Stripe — launching soon. Questions?{' '}
          <a href="mailto:tech.sushil@gmail.com" className="text-slate-400 hover:text-slate-300 transition-colors underline">
            Email us
          </a>
        </p>
      </div>
    </div>
  );
}
